import { FC, ReactNode } from 'react';
import { ButtonElement, IconContainer } from './Button.styles';

export type ButtonVariation =
  | 'primary'
  | 'newPrimary'
  | 'secondary'
  | 'secondaryDark'
  | 'tertiary'
  | 'delete'
  | 'workflowSidebarButton'
  | 'segmentationButton';

export type ButtonSize = 'small' | 'medium-small' | 'medium' | 'large';

export type ButtonIcon = string | ReactNode;

export interface IconIProps {
  direction: 'left' | 'right';
  iconColor: string;
  iconSize: string;
  variation: ButtonVariation;
}

export interface IProps extends IntrinsicProps {
  /** "primary" | "secondary" | "secondaryDark" | "tertiary" | "delete"  */
  variation?: ButtonVariation;
  /** Called when button is clicked with the event passed in */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (e?: any) => void;
  /** Defaults to false.  When true, button appears "grayed out" and cannot be clicked */
  disabled?: boolean;
  /** Button type.  Defaults to "button" */
  type?: 'submit' | 'reset' | 'button';
  /** Button ARIA label tag */
  ariaLabel?: string;
  /** Button ID */
  id?: string;
  /** Button Icon on left side */
  rightSideIcon?: ButtonIcon;
  /** Button Icon on right side */
  leftSideIcon?: ButtonIcon;
  /** Button Icon size */
  iconSize?: string;
  /** Button Icon color */
  iconColor?: string;
  /** Button Title */
  title?: string;
  /** If true the button will span the full width of it's container. */
  fullWidth?: boolean;
  /** Button width in pixels (e.g., "200px") or as a number (converted to px) */
  width?: string | number;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
}

export const Button: FC<IProps> = ({
  ariaLabel,
  type = 'button',
  variation = 'primary',
  onClick,
  disabled,
  id = '',
  rightSideIcon,
  leftSideIcon,
  iconSize,
  iconColor,
  title,
  children,
  fullWidth = false,
  width,
  ...rest
}) => (
  <ButtonElement
    data-testid="Button"
    aria-label={ariaLabel}
    type={type}
    variation={variation}
    onClick={onClick}
    disabled={disabled}
    id={id}
    title={title}
    leftSideIcon={leftSideIcon}
    rightSideIcon={rightSideIcon}
    iconSize={iconSize}
    iconColor={iconColor}
    fullWidth={fullWidth}
    width={width}
    tabIndex={0}
    {...rest}
  >
    {leftSideIcon && (
      <IconContainer direction="left" iconSize={iconSize} iconColor={iconColor} variation={variation}>
        {leftSideIcon}
      </IconContainer>
    )}
    {children}
    {title && <span className="title">{title}</span>}
    {rightSideIcon && (
      <IconContainer direction="right" iconSize={iconSize} iconColor={iconColor} variation={variation}>
        {rightSideIcon}
      </IconContainer>
    )}
  </ButtonElement>
);
